export type CustomDomainStatus = 'none' | 'pending' | 'verifying' | 'active' | 'error';

/**
 * BrandingConfig entity
 * Represents the branding configuration for the platform
 */
export class BrandingConfig {
  constructor(
    public readonly id: string,
    public readonly workspaceId: number,
    public readonly primaryColor: string,
    public readonly secondaryColor: string,
    public readonly textColor: string,
    public readonly backgroundColor: string,
    public readonly fontFamily: FontFamily,
    public readonly fontSize: number,
    public readonly fontWeight: FontWeight,
    public readonly lineHeight: number,
    public readonly letterSpacing: number,
    public readonly borderRadius: number,
    public readonly companyName?: string,
    public readonly tagline?: string,
    public readonly logoUrl?: string,
    public readonly logoLightUrl?: string,
    public readonly logoDarkUrl?: string,
    public readonly faviconUrl?: string,
    public readonly presetId?: string,
    public readonly customDomain?: string,
    public readonly customDomainStatus?: CustomDomainStatus,
    public readonly domainVerifiedAt?: Date,
    public readonly domainVerificationToken?: string,
    public readonly createdAt?: Date,
    public readonly updatedAt?: Date
  ) {}

  /**
   * Check if a custom domain is configured and active
   */
  hasActiveDomain(): boolean {
    return this.customDomainStatus === 'active' && !!this.customDomain;
  }

  /**
   * Check if domain verification is pending
   */
  isDomainPending(): boolean {
    return this.customDomainStatus === 'pending' || this.customDomainStatus === 'verifying';
  }

  /**
   * Validate a fully qualified domain name
   */
  static isValidFQDN(domain: string): boolean {
    if (!domain || domain.length > 253) return false;
    const fqdnRegex = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,}$/;
    return fqdnRegex.test(domain);
  }

  /**
   * Check if this branding uses a preset
   */
  isPresetBased(): boolean {
    return !!this.presetId;
  }

  /**
   * Get font family CSS value
   */
  getFontFamilyCSS(): string {
    return FONT_FAMILY_MAP[this.fontFamily];
  }

  /**
   * Create a copy with updated properties
   */
  update(updates: Partial<BrandingConfigProps>): BrandingConfig {
    return new BrandingConfig(
      this.id,
      this.workspaceId,
      updates.primaryColor ?? this.primaryColor,
      updates.secondaryColor ?? this.secondaryColor,
      updates.textColor ?? this.textColor,
      updates.backgroundColor ?? this.backgroundColor,
      updates.fontFamily ?? this.fontFamily,
      updates.fontSize ?? this.fontSize,
      updates.fontWeight ?? this.fontWeight,
      updates.lineHeight ?? this.lineHeight,
      updates.letterSpacing ?? this.letterSpacing,
      updates.borderRadius ?? this.borderRadius,
      updates.companyName ?? this.companyName,
      updates.tagline ?? this.tagline,
      updates.logoUrl ?? this.logoUrl,
      updates.logoLightUrl ?? this.logoLightUrl,
      updates.logoDarkUrl ?? this.logoDarkUrl,
      updates.faviconUrl ?? this.faviconUrl,
      updates.presetId ?? this.presetId,
      updates.customDomain ?? this.customDomain,
      updates.customDomainStatus ?? this.customDomainStatus,
      updates.domainVerifiedAt ?? this.domainVerifiedAt,
      updates.domainVerificationToken ?? this.domainVerificationToken,
      this.createdAt,
      new Date() // updatedAt
    );
  }

  /**
   * Convert to plain object for storage
   */
  toJSON(): BrandingConfigJSON {
    return {
      id: this.id,
      workspaceId: this.workspaceId,
      primaryColor: this.primaryColor,
      secondaryColor: this.secondaryColor,
      textColor: this.textColor,
      backgroundColor: this.backgroundColor,
      fontFamily: this.fontFamily,
      fontSize: this.fontSize,
      fontWeight: this.fontWeight,
      lineHeight: this.lineHeight,
      letterSpacing: this.letterSpacing,
      borderRadius: this.borderRadius,
      companyName: this.companyName,
      tagline: this.tagline,
      logoUrl: this.logoUrl,
      logoLightUrl: this.logoLightUrl,
      logoDarkUrl: this.logoDarkUrl,
      faviconUrl: this.faviconUrl,
      presetId: this.presetId,
      customDomain: this.customDomain,
      customDomainStatus: this.customDomainStatus,
      domainVerifiedAt: this.domainVerifiedAt?.toISOString(),
      domainVerificationToken: this.domainVerificationToken,
      createdAt: this.createdAt?.toISOString(),
      updatedAt: this.updatedAt?.toISOString(),
    };
  }

  /**
   * Create from plain object
   */
  static fromJSON(json: BrandingConfigJSON): BrandingConfig {
    return new BrandingConfig(
      json.id,
      json.workspaceId,
      json.primaryColor,
      json.secondaryColor,
      json.textColor,
      json.backgroundColor,
      json.fontFamily,
      json.fontSize,
      json.fontWeight,
      json.lineHeight,
      json.letterSpacing,
      json.borderRadius,
      json.companyName,
      json.tagline,
      json.logoUrl,
      json.logoLightUrl,
      json.logoDarkUrl,
      json.faviconUrl,
      json.presetId,
      json.customDomain,
      json.customDomainStatus as CustomDomainStatus | undefined,
      json.domainVerifiedAt ? new Date(json.domainVerifiedAt) : undefined,
      json.domainVerificationToken,
      json.createdAt ? new Date(json.createdAt) : undefined,
      json.updatedAt ? new Date(json.updatedAt) : undefined
    );
  }

  /**
   * Create default branding config
   */
  static createDefault(workspaceId: number): BrandingConfig {
    return new BrandingConfig(
      Date.now().toString(),
      workspaceId,
      '#6D5DFE', // Platform primary color
      '#10B981',
      '#1F2937',
      '#FFFFFF',
      'inter',
      16,
      500,
      1.5,
      0,
      8,
      'Your Institution',
      'Premium education platform',
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined, // customDomain
      'none', // customDomainStatus
      undefined, // domainVerifiedAt
      undefined, // domainVerificationToken
      new Date(),
      new Date()
    );
  }
}

// Types

export type FontFamily = 'inter' | 'georgia' | 'monaco';

export type FontWeight = 400 | 500 | 600 | 700;

export const FONT_FAMILY_MAP: Record<FontFamily, string> = {
  inter: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
  georgia: 'Georgia, "Times New Roman", serif',
  monaco: 'Monaco, "Courier New", monospace',
};

export const FONT_WEIGHT_OPTIONS = [
  { value: 400 as FontWeight, label: 'Regular' },
  { value: 500 as FontWeight, label: 'Medium' },
  { value: 600 as FontWeight, label: 'Semibold' },
  { value: 700 as FontWeight, label: 'Bold' },
] as const;

interface BrandingConfigProps {
  primaryColor?: string;
  secondaryColor?: string;
  textColor?: string;
  backgroundColor?: string;
  fontFamily?: FontFamily;
  fontSize?: number;
  fontWeight?: FontWeight;
  lineHeight?: number;
  letterSpacing?: number;
  borderRadius?: number;
  companyName?: string;
  tagline?: string;
  logoUrl?: string;
  logoLightUrl?: string;
  logoDarkUrl?: string;
  faviconUrl?: string;
  presetId?: string;
  customDomain?: string;
  customDomainStatus?: CustomDomainStatus;
  domainVerifiedAt?: Date;
  domainVerificationToken?: string;
}

interface BrandingConfigJSON {
  id: string;
  workspaceId: number;
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  backgroundColor: string;
  fontFamily: FontFamily;
  fontSize: number;
  fontWeight: FontWeight;
  lineHeight: number;
  letterSpacing: number;
  borderRadius: number;
  companyName?: string;
  tagline?: string;
  logoUrl?: string;
  logoLightUrl?: string;
  logoDarkUrl?: string;
  faviconUrl?: string;
  presetId?: string;
  customDomain?: string;
  customDomainStatus?: string;
  domainVerifiedAt?: string;
  domainVerificationToken?: string;
  createdAt?: string;
  updatedAt?: string;
}
